import BlogPostPage from "@/src/pages/consumer/BlogPostPage";

import {
  combineArticleBodyText,
  convertDateToIsoDatetime,
  getArticleUrl
} from "@/src/utils/blogUtils";
import { createBaanaPublisher } from "@/src/utils/seo/builders";
import { toCanonicalUrl } from "@/src/utils/siteUrl";

import { loadArticleRouteData } from "../articleRouteData";
import { useHydrateArticleAtoms } from "../useHydrateArticleAtoms";
import type { Route } from "./+types/blog-post";

// Runs at build time (ssr:false + prerender) to render the static HTML and,
// crucially, power the SEO meta tags + JSON-LD below. Because this data drives
// the initial render, `meta()` receives the real article at prerender so the
// static document carries the correct <title>, description, OG tags and
// structured data for crawlers and social scrapers. A missing article aborts
// prerendering with a 404 so we never emit a broken static page.
export async function loader({ params }: Route.LoaderArgs) {
  const data = await loadArticleRouteData();
  const article = data.articles[params.title ?? ""];

  if (!article) {
    throw new Response("Not Found", { status: 404 });
  }

  return { ...data, article };
}

// Runs in the browser for client-side navigations and for articles published
// after the last build (not prerendered). For prerendered articles the
// build-time loader data is inlined into the document and returned by
// `serverLoader()`, so we reuse it; otherwise we resolve the list on the client
// and degrade gracefully. Deliberately no `hydrate = true` / `HydrateFallback`:
// those force the initial render to a fallback and leave `meta()` without data,
// which is exactly what breaks SEO on the prerendered pages.
export async function clientLoader({
  params,
  serverLoader
}: Route.ClientLoaderArgs) {
  try {
    const serverData = (await serverLoader()) as Awaited<
      ReturnType<typeof loader>
    >;
    if (serverData?.article) {
      return serverData;
    }
  } catch {
    // No prerendered data for this path (e.g. a brand-new article); fall
    // through to resolving the article list on the client.
  }

  const data = await loadArticleRouteData();
  const article = data.articles[params.title ?? ""];

  return { ...data, article };
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  if (!loaderData?.article) return [{ title: "Not Found - Baana" }];

  const { article } = loaderData;
  const slug = getArticleUrl(article.title);
  const canonicalUrl = toCanonicalUrl(`/blog/${slug}`);

  return [
    { title: `${article.title} - Baana` },
    { name: "description", content: article.ingress },
    { property: "og:title", content: article.title },
    { property: "og:description", content: article.ingress },
    { property: "og:image", content: article.mainImage.url },
    { property: "og:type", content: "article" },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
        headline: article.title,
        description: article.ingress,
        articleBody: combineArticleBodyText(article),
        image: [article.mainImage.url],
        author: {
          "@type": "Organization",
          name: "Baana",
          url: toCanonicalUrl("/")
        },
        publisher: createBaanaPublisher(),
        datePublished: convertDateToIsoDatetime(article.date),
        dateModified: convertDateToIsoDatetime(
          article.dateModified ?? article.date
        ),
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Artikkelit",
              item: toCanonicalUrl("/blog")
            },
            {
              "@type": "ListItem",
              position: 2,
              name: article.title,
              item: canonicalUrl
            }
          ]
        },
        inLanguage: "fi"
      }
    }
  ];
};

export default function BlogPostRoute({ loaderData }: Route.ComponentProps) {
  useHydrateArticleAtoms(loaderData);

  return <BlogPostPage />;
}
